// THE ROUTE CONTRACT (UNIFIED-FUNCTION F1) — one parser, one destination table, one navigator.
//
// Four studies independently specified the same change to App.tsx's hash parser, and every one
// of them emitted routes into pages that were never built to receive them. This module is the
// single answer: where a destination is named, it is named here; where a page is arrived at
// with intent, it reads that intent through `useDeepLink`.
//
// THREE LAWS, and the reasons they exist:
//
//   1. A PARAM NEVER REMOUNTS A PAGE. `key` is the page id alone. Lifecycle D4 proposed
//      `key: id + '?' + query`, which would destroy Proposals' in-session disposition ledger
//      (and any other page-local state) every time another tab routed in.
//      EXCEPTION, deliberate: the client detail route keeps its composite key `client:N`, so
//      #client?id=7 → #client?id=3 still resets scroll and still remounts ClientPage. That is
//      a different client, not a filter (FEASIBILITY G-10).
//
//   2. PARAMS ARE ONE-SHOT ARRIVAL INTENTS. The receiving page applies them once and then
//      clears the query, so (a) the page owns its filter state afterwards, (b) re-clicking the
//      same link fires again, (c) back/forward never replays a stale filter. Unknown params are
//      ignored. Filters therefore never persist across navigation (B4).
//
//   3. CONTEXT BEFORE HASH. An engagement-scoped destination sets EngagementContext.current
//      FIRST and only then changes the hash — and it waits for the engagement list to arrive,
//      so a click from a cold page cannot land on a page whose `current` is still null
//      (FEASIBILITY G-9).
//
// This module is deliberately DEPENDENCY-FREE at runtime — no React components, no registry
// import, no `window` access at module scope — so the exhaustiveness test can import it.

import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import type { PageId } from './pages/registry.js';

// ---------------------------------------------------------------------------
// The destination
// ---------------------------------------------------------------------------

/**
 * One resolved destination. Built only by a `ROUTES.*` constructor — never by hand, never by
 * string concatenation, so a typo in a hash cannot reach a page.
 */
export interface Dest {
  page: PageId;
  /** One-shot arrival intents. Already stringified; empty values are dropped by the builder. */
  params: Record<string, string>;
  /** Engagement-scoped destination: set as `current` BEFORE the hash changes. */
  engagementId?: number | null;
  /** The client detail route only (`#client?id=N`). */
  clientId?: number;
}

/** The parsed current location. */
export interface Route {
  page: PageId;
  clientId: number | null;
  params: URLSearchParams;
  /** What the scroll reset watches — see law 1. */
  key: string;
}

const put = (
  out: Record<string, string>,
  key: string,
  value: string | number | boolean | null | undefined,
): void => {
  // An absent intent and an empty intent are the same thing: no intent. `false` and `0` are
  // real values and survive.
  if (value === null || value === undefined || value === '') return;
  out[key] = String(value);
};

const build = (
  page: PageId,
  spec: Record<string, string | number | boolean | null | undefined>,
  engagementId?: number | null,
): Dest => {
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(spec)) put(params, k, v);
  return { page, params, engagementId };
};

/** Engagement-scoped destinations all accept `engagement` — the context to open them in. */
interface Scoped {
  engagement?: number | null;
  /** An in-page anchor at the destination. Travels as a PARAM, never as a second '#'. */
  anchor?: string | null;
}

/**
 * THE DESTINATION TABLE. Lifecycle's `COND_ROUTE` values, contracting's fix column, admin's
 * `narrate()` table and dashboard's per-cell constants all become calls into this object. Those
 * tables keep their KEYS — which rule routes where is per-tab knowledge — but never their values.
 */
export const ROUTES = {
  dashboard: (o: { tasks?: string | null } = {}): Dest => build('dashboard', { tasks: o.tasks }),

  clients: (): Dest => build('clients', {}),

  /** The one parameterized detail route. `id` is not an arrival intent — it IS the address. */
  client: (o: { id: number; anchor?: string | null }): Dest => ({
    page: 'clients',
    clientId: o.id,
    params: o.anchor ? { anchor: o.anchor } : {},
  }),

  lifecycle: (o: Scoped = {}): Dest => build('lifecycle', { anchor: o.anchor }, o.engagement),

  documents: (o: Scoped & { status?: string | null } = {}): Dest =>
    build('documents', { status: o.status, anchor: o.anchor }, o.engagement),

  register: (
    o: Scoped & {
      status?: string | null;
      ref?: string | null;
      ac?: string | null;
      tab?: string | null;
      gap?: string | null;
      priority?: string | null;
      document?: number | string | null;
    } = {},
  ): Dest =>
    build(
      'register',
      {
        status: o.status, ref: o.ref, ac: o.ac, tab: o.tab,
        gap: o.gap, priority: o.priority, document: o.document, anchor: o.anchor,
      },
      o.engagement,
    ),

  agent: (o: Scoped & { docs?: string | number[] | null } = {}): Dest =>
    build(
      'agent',
      { docs: Array.isArray(o.docs) ? o.docs.join(',') : o.docs, anchor: o.anchor },
      o.engagement,
    ),

  /**
   * Proposals lives INSIDE the Agent page now — its 'proposals' sub-tab. The constructor keeps
   * its signature (every caller stays untouched) and emits '#agent?tab=proposals&…': AgentPage's
   * useDeepLink opens the tab, and ProposalsPage — mounted as the tab's child — reads `run` and
   * `status` itself through the same RouteParamsContext.
   */
  proposals: (o: Scoped & { run?: number | string | null; status?: string | null } = {}): Dest =>
    build('agent', { tab: 'proposals', run: o.run, status: o.status, anchor: o.anchor }, o.engagement),

  decks: (o: Scoped & { deck?: number | string | null } = {}): Dest =>
    build('decks', { deck: o.deck, anchor: o.anchor }, o.engagement),

  contracting: (o: Scoped & { tab?: string | null } = {}): Dest =>
    build('contracting', { tab: o.tab, anchor: o.anchor }, o.engagement),

  acceptance: (o: Scoped & { tab?: string | null } = {}): Dest =>
    build('acceptance', { tab: o.tab, anchor: o.anchor }, o.engagement),

  portalAdmin: (o: Scoped & { thread?: number | string | null } = {}): Dest =>
    build('portal-admin', { thread: o.thread, anchor: o.anchor }, o.engagement),

  admin: (o: { tab?: string | null; engagement?: number | null } = {}): Dest =>
    build('admin', { tab: o.tab, engagement: o.engagement }),
} as const;

/**
 * Every page a `ROUTES.*` constructor can reach. Compile-time exhaustiveness: if a page is added
 * to the registry and no constructor is written for it, the `satisfies` below still passes — but
 * the assignment under it does not, and `bunx tsc --noEmit` says so.
 */
export const ROUTED_PAGES = [
  'dashboard', 'clients', 'lifecycle', 'documents', 'register',
  'agent', 'decks', 'contracting', 'acceptance', 'portal-admin', 'admin',
] as const satisfies readonly PageId[];

/** Fails to compile the moment a PageId exists that ROUTED_PAGES does not list. */
const _everyPageIsRoutable: (typeof ROUTED_PAGES)[number] extends PageId
  ? PageId extends (typeof ROUTED_PAGES)[number]
    ? true
    : never
  : never = true;
void _everyPageIsRoutable;

// ---------------------------------------------------------------------------
// Hash ↔ Dest
// ---------------------------------------------------------------------------

/**
 * A destination as an address. Note what this does NOT emit: a second '#'. The old
 * `openScreen(screen, id, anchor)` composed `#register#questions`, which the parser reads as the
 * page named "register#questions" — i.e. no page at all, silently falling back to the dashboard.
 * Anchors travel as the `anchor` param and are applied by the receiver.
 */
export function toHash(dest: Dest): string {
  const head = dest.clientId ? 'client' : dest.page;
  const query = new URLSearchParams(dest.clientId ? { id: String(dest.clientId) } : {});
  for (const [k, v] of Object.entries(dest.params)) query.set(k, v);
  const q = query.toString();
  return q ? `#${head}?${q}` : `#${head}`;
}

/** For `href` on a real anchor element — right-click/open-in-new-tab keep working. */
export const href = (dest: Dest): string => toHash(dest);

/**
 * Parse a raw hash body (no leading '#').
 *
 * `resolvePage` is injected rather than imported so this module stays free of the page registry
 * — which imports every page component, and through them React, the icon set and the whole API
 * layer. A route parser must be testable without booting the application.
 */
export function parseRoute(raw: string, resolvePage: (head: string) => PageId): Route {
  const [head, query = ''] = raw.split('?');
  const params = new URLSearchParams(query);
  if (head === 'client') {
    const clientId = Number(params.get('id'));
    if (Number.isInteger(clientId) && clientId > 0) {
      // Composite key: two clients are two different subjects, not one page with a filter.
      return { page: 'clients', clientId, params, key: `client:${clientId}` };
    }
    // '#client' without a usable id has nothing to show — the list is the fallback.
    return { page: 'clients', clientId: null, params: new URLSearchParams(), key: 'clients' };
  }
  const page = resolvePage(head);
  // Law 1: the key is the page id. Params change; the page does not remount.
  return { page, clientId: null, params, key: page };
}

// ---------------------------------------------------------------------------
// Server-emitted routes
// ---------------------------------------------------------------------------

/**
 * The `RouteRef` shape briefQueries.ts emits: `{kind:'screen'|'in-page'|'related', …}`. The
 * server never emits a hash string — it emits a reference, and this is the one place it is
 * resolved. Two kinds are NOT page destinations and are handed back to the caller:
 *
 *   in-page   scroll to an anchor on the surface that is already open
 *   screen: 'timeline' | 'engagements'   the client page's own sub-views
 */
export type Resolved =
  | { kind: 'dest'; dest: Dest }
  | { kind: 'in-page'; anchor: string }
  | { kind: 'client-view'; view: 'timeline' | 'engagements'; anchor: string | null }
  | { kind: 'none'; reason: string };

export function resolveRouteRef(ref: Record<string, unknown> | null): Resolved {
  if (!ref) return { kind: 'none', reason: 'This item carries no route.' };
  const kind = String(ref.kind ?? '');

  if (kind === 'in-page') return { kind: 'in-page', anchor: String(ref.anchor ?? '') };

  if (kind === 'related') {
    const related = (ref.related ?? {}) as Record<string, unknown>;
    if (!related.screen) {
      return { kind: 'none', reason: 'This task carries no route — open it on the engagement it belongs to.' };
    }
    return resolveRouteRef({ ...related, kind: 'screen' });
  }

  if (kind !== 'screen') return { kind: 'none', reason: `Unknown route kind '${kind}'.` };

  const screen = String(ref.screen ?? '');
  const engagement = ref.engagement_id === undefined ? null : Number(ref.engagement_id);
  const anchor = ref.anchor === undefined || ref.anchor === null ? null : String(ref.anchor);

  if (screen === 'timeline' || screen === 'engagements') {
    return { kind: 'client-view', view: screen, anchor };
  }

  const scoped = { engagement, anchor };
  switch (screen) {
    case 'dashboard': return { kind: 'dest', dest: ROUTES.dashboard() };
    case 'clients': return { kind: 'dest', dest: ROUTES.clients() };
    case 'lifecycle': return { kind: 'dest', dest: ROUTES.lifecycle(scoped) };
    case 'documents': return { kind: 'dest', dest: ROUTES.documents(scoped) };
    case 'register': return { kind: 'dest', dest: ROUTES.register(scoped) };
    case 'agent': return { kind: 'dest', dest: ROUTES.agent(scoped) };
    // 'proposals' refs land on the Agent page's proposals tab — the constructor carries the tab.
    case 'proposals': return { kind: 'dest', dest: ROUTES.proposals(scoped) };
    case 'decks': return { kind: 'dest', dest: ROUTES.decks(scoped) };
    case 'contracting': return { kind: 'dest', dest: ROUTES.contracting(scoped) };
    case 'acceptance': return { kind: 'dest', dest: ROUTES.acceptance(scoped) };
    case 'portal-admin': return { kind: 'dest', dest: ROUTES.portalAdmin(scoped) };
    case 'admin': return { kind: 'dest', dest: ROUTES.admin({ engagement }) };
    default: return { kind: 'none', reason: `No page is named '${screen}'.` };
  }
}

// ---------------------------------------------------------------------------
// Navigating, and arriving
// ---------------------------------------------------------------------------

/** What `useOpenRoute` needs from EngagementContext — declared structurally, not imported. */
export interface EngagementNav {
  setCurrentId: (id: number | null) => void;
  /** Resolves once the engagement list has arrived (or failed). Law 3. */
  ensureLoaded: () => Promise<unknown>;
}

/**
 * B3 · "Open in context". The lift of ClientPage's `openScreen` that dashboard and clients both
 * asked for, with the cold-page race closed: the engagement list is awaited before `current` is
 * set, so the destination never mounts with `current === null`.
 *
 * The smoke test this must satisfy (dashboard §11.5): from a cold page, one click lands with
 * `current.code` equal to the code printed on the control that was clicked.
 *
 * KNOWN CONSEQUENCE, accepted: `setCurrentId` persists to localStorage, so routing into another
 * client's engagement does repoint the operator's selection. That is what "open in context"
 * means; it is not a side effect to be hidden.
 */
export function makeOpenRoute(nav: EngagementNav): (dest: Dest) => Promise<void> {
  return async (dest: Dest) => {
    if (dest.engagementId !== null && dest.engagementId !== undefined) {
      await nav.ensureLoaded();
      nav.setCurrentId(dest.engagementId);
    }
    window.location.hash = toHash(dest);
  };
}

/** Hook form. Pass the context object; the identity of the returned function is stable per nav. */
export function useOpenRoute(nav: EngagementNav): (dest: Dest) => Promise<void> {
  const { setCurrentId, ensureLoaded } = nav;
  return useCallback(
    (dest: Dest) => makeOpenRoute({ setCurrentId, ensureLoaded })(dest),
    [setCurrentId, ensureLoaded],
  );
}

/**
 * The arrival intents of the CURRENT route, published to whatever page is mounted. Pages are
 * rendered from the registry with no props (`<PageComponent />`), so a receiver reads its
 * params from context rather than from a signature every page would have to grow.
 */
const EMPTY_PARAMS = new URLSearchParams();
export const RouteParamsContext = createContext<URLSearchParams>(EMPTY_PARAMS);
export const useRouteParams = (): URLSearchParams => useContext(RouteParamsContext);

export type DeepLinkHandlers = Record<string, (value: string) => void>;

/**
 * Law 2, in one hook. A receiving page declares which arrival intents it honours; everything
 * else on the query string is ignored, not guessed at.
 *
 *   useDeepLink(route.params, {
 *     status:   (v) => setStatusFilter(v),
 *     ref:      (v) => selectRow(v),
 *     anchor:   (v) => scrollToAnchor(v),     // handled here by default if not declared
 *   });
 *
 * After the handlers run the query is cleared with `replaceState` — which does NOT fire
 * `hashchange`, so the page keeps the state its handlers just set.
 */
export function useDeepLink(params: URLSearchParams, handlers: DeepLinkHandlers): void {
  const applied = useRef<string | null>(null);
  const query = params.toString();
  // The handler table is re-created on every render by every honest caller; freezing it in a ref
  // keeps this effect keyed on the QUERY alone, which is what "one-shot" means.
  const latest = useRef(handlers);
  latest.current = handlers;

  useEffect(() => {
    if (!query || applied.current === query) return;
    applied.current = query;
    const arrived = new URLSearchParams(query);
    for (const [key, value] of arrived) {
      const handler = latest.current[key];
      if (handler) handler(value);
      else if (key === 'anchor') scrollToAnchor(value);
    }
    const [head] = window.location.hash.slice(1).split('?');
    // Keep the client detail route addressable: its `id` is the address, not an intent.
    const keep = head === 'client' && arrived.get('id') ? `?id=${arrived.get('id')}` : '';
    window.history.replaceState(null, '', `#${head}${keep}`);
  }, [query]);
}

/** Anchors arrive as ids with or without the '#'; both forms are accepted. */
export function scrollToAnchor(anchor: string): void {
  if (!anchor) return;
  const id = anchor.startsWith('#') ? anchor.slice(1) : anchor;
  // The receiver has just rendered from a state update; wait one frame for it to exist.
  requestAnimationFrame(() => {
    const node = document.getElementById(id) ?? document.querySelector(`[data-anchor="${id}"]`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}
